import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  fetchMailboxes,
  disconnectMailbox,
  beginOAuthConnect,
  oauthProviderForMailbox,
  type ConnectedMailbox,
} from '@/lib/mailboxApi';
import { getApiErrorMessage } from '@/lib/api';
import { OAuthReturnNotice } from '@/components/shared/OAuthReturnNotice';

/**
 * CONNECTED ACCOUNTS — connect, see, and DISCONNECT your own mailbox.
 *
 * ── WHY THIS EXISTS ───────────────────────────────────────────────────────────────────────
 * `disconnectMailbox()` lived in `lib/mailboxApi.ts` and `DELETE /mailboxes/:id` worked and was
 * correctly scoped — but nothing in the web app ever called it. A barrister who connected the
 * wrong Google account, or who simply wanted Jurivoxa to stop reading their mail, had no way to
 * sever it from inside the product: the refresh token stayed in the database and the 30-second
 * poll kept running. Connect had a button; disconnect did not.
 *
 * That asymmetry is not a missing nicety on a legal platform. Revoking a processor's access to
 * your correspondence is the kind of control a barrister must be able to exercise themselves, at
 * the moment they decide to — not by emailing support. (CLAUDE.md Hard Rule #18 names this exact
 * dead end as a live example; this is its fix.)
 *
 * Deliberately placed in the barrister's OWN email settings, not the admin console: it is their
 * mailbox and their consent to withdraw.
 */

const PROVIDER_LABEL: Record<ConnectedMailbox['provider'], string> = {
  gmail: 'Gmail',
  google_workspace: 'Google Workspace',
  microsoft_365: 'Microsoft 365',
  exchange: 'Exchange',
  imap: 'IMAP',
};

const STATUS_LABEL: Record<ConnectedMailbox['status'], string> = {
  active: 'Connected',
  disconnected: 'Disconnected',
  error: 'Needs reconnecting',
  revoked: 'Access revoked',
};

function ConfirmDisconnect({
  mailbox,
  onCancel,
  onConfirm,
  pending,
}: {
  mailbox: ConnectedMailbox;
  onCancel: () => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Disconnect ${mailbox.emailAddress}`}
      style={{
        position: 'fixed', inset: 0, zIndex: 200, display: 'flex',
        alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', padding: 16,
      }}
    >
      <div style={{
        maxWidth: 460, width: '100%', background: 'var(--surface-card)',
        border: '1px solid var(--border-default)', borderRadius: 10, padding: 24,
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
          Disconnect {mailbox.emailAddress}?
        </h2>
        {/*
          Say exactly what happens, including what does NOT happen. A barrister disconnecting a
          mailbox needs to know their filed correspondence is not being deleted underneath them.
        */}
        <ul style={{ fontSize: 13, color: 'var(--text-muted)', margin: '12px 0 0', paddingLeft: 18, lineHeight: 1.7 }}>
          <li>Jurivoxa stops reading and sending from this account immediately.</li>
          <li>The stored sign-in is deleted — reconnecting requires your consent again.</li>
          <li>Email already synced into matters and their history stays where it is.</li>
        </ul>
        <p style={{ fontSize: 12, color: 'var(--text-subtle)', margin: '12px 0 0' }}>
          To remove Jurivoxa&apos;s access at Google as well, also revoke it at
          {' '}myaccount.google.com/permissions.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button
            onClick={onCancel}
            style={{
              fontSize: 13, padding: '7px 14px', borderRadius: 6, cursor: 'pointer',
              border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-primary)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={pending}
            style={{
              fontSize: 13, fontWeight: 600, padding: '7px 14px', borderRadius: 6,
              cursor: pending ? 'not-allowed' : 'pointer', border: '1px solid #b91c1c',
              background: '#b91c1c', color: '#fff', opacity: pending ? 0.7 : 1,
            }}
          >
            {pending ? 'Disconnecting…' : 'Disconnect mailbox'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ConnectedAccountsSection() {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<ConnectedMailbox | null>(null);

  const { data: mailboxes = [], isLoading } = useQuery({
    queryKey: ['mailboxes'],
    queryFn: fetchMailboxes,
  });

  const disconnectMutation = useMutation({
    mutationFn: (id: string) => disconnectMailbox(id),
    onSuccess: () => {
      toast.success('Mailbox disconnected. Jurivoxa no longer has access to that account.');
      setConfirming(null);
      // Every surface that reads mailbox state must move together — inbox banner, folder
      // counts, health strip — or the UI claims a connection that no longer exists.
      queryClient.invalidateQueries({ queryKey: ['mailboxes'] });
      queryClient.invalidateQueries({ queryKey: ['mailbox-health'] });
      queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not disconnect the mailbox')),
  });

  async function connect(provider: 'google' | 'microsoft') {
    try {
      window.location.href = await beginOAuthConnect(provider);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not start the connection'));
    }
  }

  // RECONNECT, not a fresh connection. `mailboxId` is sealed into the signed OAuth state so the
  // server refuses a different provider account; `returnTo` brings the user back to this page
  // instead of dropping them in the inbox. Without both, every Reconnect silently created a
  // second connection and landed somewhere else.
  async function reconnect(m: ConnectedMailbox) {
    const provider = oauthProviderForMailbox(m.provider);
    if (!provider) return;
    try {
      window.location.href = await beginOAuthConnect(provider, { mailboxId: m.id, returnTo: 'email-settings' });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not start the reconnection'));
    }
  }

  return (
    <section style={{ marginBottom: 28 }} aria-labelledby="connected-accounts-heading">
      <OAuthReturnNotice connectedText="The mailbox is connected." />
      <h2
        id="connected-accounts-heading"
        style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 10px' }}
      >
        Connected accounts
      </h2>

      {isLoading ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>Loading…</div>
      ) : mailboxes.length === 0 ? (
        <div style={{
          border: '1px solid var(--border-default)', background: 'var(--surface-card)',
          borderRadius: 8, padding: '16px 20px',
        }}>
          <div style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 500 }}>No mailbox connected</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
            Connect an account to read and send email from inside Jurivoxa.
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button onClick={() => connect('google')} style={connectBtn}>Connect Gmail / Google Workspace</button>
            <button onClick={() => connect('microsoft')} style={connectBtn}>Connect Microsoft 365</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {mailboxes.map((mb) => (
            <div
              key={mb.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
                padding: '14px 20px', borderRadius: 8,
                border: '1px solid var(--border-default)', background: 'var(--surface-card)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
                  {mb.emailAddress}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                  {PROVIDER_LABEL[mb.provider] ?? mb.provider} · {STATUS_LABEL[mb.status] ?? mb.status}
                </div>
              </div>
              {oauthProviderForMailbox(mb.provider) && (
                <button
                  onClick={() => void reconnect(mb)}
                  aria-label={`Reconnect ${mb.emailAddress}`}
                  style={{
                    fontSize: 13, fontWeight: 500, padding: '6px 12px', borderRadius: 6,
                    cursor: 'pointer', border: '1px solid var(--border-default)', background: 'transparent',
                    color: 'var(--text-primary)', flexShrink: 0,
                  }}
                >
                  Reconnect
                </button>
              )}
              <button
                onClick={() => setConfirming(mb)}
                aria-label={`Disconnect ${mb.emailAddress}`}
                style={{
                  fontSize: 13, fontWeight: 500, padding: '6px 12px', borderRadius: 6,
                  cursor: 'pointer', border: '1px solid #b91c1c', background: 'transparent',
                  color: '#b91c1c', flexShrink: 0,
                }}
              >
                Disconnect
              </button>
            </div>
          ))}
        </div>
      )}

      {confirming && (
        <ConfirmDisconnect
          mailbox={confirming}
          pending={disconnectMutation.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => disconnectMutation.mutate(confirming.id)}
        />
      )}
    </section>
  );
}

const connectBtn: React.CSSProperties = {
  fontSize: 13, fontWeight: 500, padding: '7px 14px', borderRadius: 6, cursor: 'pointer',
  border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-primary)',
};
